import { IBreadcrumb } from '../../components/breadcrumbs'
import {
  createProductListingCategoryBreadcrumbs,
  createProductSearchBreadcrumbs,
} from '../../lib/breadcrumbs'
import { generatePathForCategoryIdPath } from '../../lib/marketplace-categories'
import { IFacet, IFacetSelections } from '../../types/facets'
import {
  ICategory,
  ICategoryPathElement,
} from '../../types/marketplace-categories'

export function selectedfacet(
  facetSelections: IFacetSelections | undefined,
  label: string
): string | undefined {
  const facetSelection = facetSelections?.find(item => item.id === label)
    ?.selections
  return facetSelection?.length === 1 ? facetSelection[0] : undefined
}

export function createDescription(
  categoryTitle: string,
  categoryH1Title: string,
  category: ICategory,
  enableBrandUpdate?: boolean
): string {
  const brandText = enableBrandUpdate ? 'Westfield' : 'Westfield Direct'
  if (categoryTitle !== categoryH1Title) {
    return `Shop the best of ${categoryH1Title} at ${brandText}. Browse over our wide range of ${categoryH1Title} products.`
  }

  return (
    category.content?.metaDescription ||
    `Find the latest${
      category.path.length ? ` ${category.title}` : ''
    } from top brands from a Westfield retailer`
  )
}

export function makeProductIndexBreadcrumbs(
  basePath: string,
  breadcrumbSearchParams: string,
  query: string,
  breadcrumbTitle?: string,
  path?: ICategoryPathElement[],
  allowAutoSurface?: boolean
): IBreadcrumb[] {
  const baseBreadcrumbs: IBreadcrumb[] = [
    {
      title: 'Home',
      linkTo: '/shop',
    },
  ]

  // add breadcrumb on auto surfaced product collection page which goes to product collections listing page
  if (basePath.includes('/shop/collection') && allowAutoSurface) {
    baseBreadcrumbs.push({
      title: 'Curated Collections',
      linkTo: '/shop/collections',
    })
  }

  // add title breadcrumbs for brands and collections
  if (breadcrumbTitle) {
    baseBreadcrumbs.push({
      title: breadcrumbTitle,
      linkTo: path?.length
        ? {
            pathname: generatePathForCategoryIdPath(basePath, []),
            search: breadcrumbSearchParams,
          }
        : undefined,
    })
  }

  if (query) {
    baseBreadcrumbs.push(...createProductSearchBreadcrumbs(query))
  }

  return createProductListingCategoryBreadcrumbs(
    baseBreadcrumbs,
    basePath,
    path ?? [],
    breadcrumbSearchParams
  )
}

export function calculatePagination(
  page: number,
  itemsPerPage: number,
  numberOfProducts: number
): {
  start: number
  end: number
  numberOfPages: number
} {
  const start = page * itemsPerPage + 1
  const end = Math.min(start + itemsPerPage - 1, numberOfProducts)
  const numberOfPages = Math.ceil(numberOfProducts / itemsPerPage)

  return { start, end, numberOfPages }
}

export function shouldIndexPlpPage(facetSelections: IFacetSelections): boolean {
  const noFacetSelected = facetSelections.length === 0

  const brand = selectedfacet(facetSelections, 'brand')
  const store = selectedfacet(facetSelections, 'store')
  const colour = selectedfacet(facetSelections, 'colour')
  const material = selectedfacet(facetSelections, 'material')
  const occasion = selectedfacet(facetSelections, 'occasion')

  return (
    noFacetSelected || Boolean(brand || store || colour || material || occasion)
  )
}

export function createCategoryH1Title(
  categoryTitle: string,
  onSale: boolean,
  facetSelections?: IFacetSelections
): string {
  const brand = selectedfacet(facetSelections, 'brand')
  const store = selectedfacet(facetSelections, 'store')
  const colour = selectedfacet(facetSelections, 'colour')
  const material = selectedfacet(facetSelections, 'material')
  const occasion = selectedfacet(facetSelections, 'occasion')

  const genders = ["Men's", "Women's", "Girls'", "Boys'"]
  const gender = genders.find(el => categoryTitle.indexOf(el) != -1)

  let h1Title = categoryTitle

  if (gender && h1Title.length !== gender.length) {
    const insertIndex = h1Title.indexOf(gender) + gender.length

    h1Title = [
      h1Title.slice(0, insertIndex),
      colour,
      material,
      occasion,
      h1Title.slice(insertIndex + 1),
    ]
      .filter(Boolean)
      .join(' ')
  } else {
    h1Title = colour ? `${colour} ${h1Title}` : h1Title
  }

  if (brand) {
    h1Title = `${brand} ${h1Title}`
  }

  if (store && brand != store) {
    h1Title = `${h1Title} from ${store}`
  }

  if (onSale) {
    h1Title = `${h1Title} On Sale`
  }

  return h1Title.trim()
}

export function createPageTitle(
  categoryTitle: string,
  categoryH1Title: string,
  category: ICategory,
  pageTitle?: string,
  query?: string,
  enableBrandUpdate?: boolean
): string {
  const brandTitleText = enableBrandUpdate ? 'Westfield' : 'Westfield Direct'
  if (query && query?.length > 0) {
    return `Shop for "${query}" in ${categoryTitle} | ${brandTitleText}`
  }

  if (categoryTitle !== categoryH1Title && categoryTitle !== 'Shop all') {
    return `Shop ${categoryH1Title} Online | ${brandTitleText}`
  }

  if (pageTitle) {
    return pageTitle
  }

  return `${categoryTitle} | ${category.title} on ${brandTitleText}`
}

// validateFacetSelections is being used for H1 title, page title and description and indexing of plp page
// for all above, we ignore any multiple selection on one facet
// That is the reason "selections[0]" has been used here for the comparison
export function validateFacetSelections(
  urlFacetSelections: IFacetSelections,
  dataFacets: IFacet[] | undefined
): IFacetSelections {
  if (!dataFacets) return []
  return urlFacetSelections.filter(urlFacet =>
    dataFacets.some(
      dataFacet =>
        dataFacet.id === urlFacet.id &&
        dataFacet.options.some(facet => facet.id === urlFacet.selections[0])
    )
  )
}

export function createCategoryTitle(category: ICategory): string {
  return category.content?.h1Header ?? category.title
}

export function createProductsHeader(
  category: ICategory,
  validFacetSelections: IFacetSelections,
  onSale: boolean,
  query?: string,
  dataCategoryPageTitle?: string,
  enableBrandUpdate?: boolean
): {
  heading: string
  title: string
  description: string
} {
  const categoryTitle = createCategoryTitle(category)
  const categoryH1Title = createCategoryH1Title(
    categoryTitle,
    onSale,
    validFacetSelections
  )

  const pageHeading =
    query && query.length > 0
      ? `Results for "${query}" in ${categoryTitle}`
      : categoryH1Title

  return {
    heading: pageHeading,
    title: createPageTitle(
      categoryTitle,
      categoryH1Title,
      category,
      dataCategoryPageTitle,
      query,
      enableBrandUpdate
    ),
    description: createDescription(
      categoryTitle,
      categoryH1Title,
      category,
      enableBrandUpdate
    ),
  }
}
